import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { usePrepAuth } from '../contexts/PrepAuthContext';

export const PrepHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, logout } = usePrepAuth();

  const navItems = [
    { path: '/prep', label: 'Home' },
    { path: '/prep/programs', label: 'Certification Prep' },
    { path: '/prep/individual', label: 'Individual Options' },
    { path: '/prep/dashboard', label: 'Dashboard', protected: true },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-gradient-to-r from-blue-900 to-blue-700 border-b border-blue-600 sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Bar - Back to Main Site */}
        <div className="border-b border-blue-600 py-2">
          <Link to="/" className="text-blue-200 hover:text-white text-sm transition-colors">
            ← Back to Main Site
          </Link>
        </div>

        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/prep" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_push-life-rescue/artifacts/p11bt5xn_IMG_1085.PNG" 
              alt="PUSH Training Prep Logo" 
              className="h-14 w-auto"
            />
            <div className="text-white">
              <div className="text-xl font-bold">Certification Prep</div>
              <div className="text-xs text-blue-200">Allied Health Certification Prep</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              if (item.protected && !isAuthenticated) return null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-white bg-blue-800'
                      : 'text-blue-100 hover:text-white hover:bg-blue-800'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            {isAuthenticated ? (
              <div className="flex items-center gap-3 ml-4">
                {user?.role === 'admin' && (
                  <Link
                    to="/prep/admin"
                    className="px-4 py-2 rounded-md text-sm font-medium bg-white text-blue-900 hover:bg-blue-50 transition-colors"
                  >
                    Admin Panel
                  </Link>
                )}
                <div className="flex items-center gap-2 text-blue-100">
                  <User size={18} />
                  <span className="text-sm">{user?.name || 'User'}</span>
                </div>
                <Button 
                  onClick={logout}
                  variant="outline" 
                  size="sm"
                  className="border-blue-300 text-blue-100 hover:bg-blue-800 hover:text-white"
                >
                  <LogOut size={16} className="mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <Link to="/prep/login">
                <Button className="ml-4 bg-white text-blue-900 hover:bg-blue-50">
                  Login
                </Button>
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-blue-100 hover:bg-blue-800 transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 border-t border-blue-600">
            {navItems.map((item) => {
              if (item.protected && !isAuthenticated) return null;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-white bg-blue-800'
                      : 'text-blue-100 hover:bg-blue-800'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="px-4 pt-2">
              {isAuthenticated ? (
                <Button 
                  onClick={logout}
                  className="w-full bg-white text-blue-900 hover:bg-blue-50"
                >
                  <LogOut size={16} className="mr-2" />
                  Logout
                </Button>
              ) : (
                <Link to="/prep/login">
                  <Button className="w-full bg-white text-blue-900 hover:bg-blue-50">
                    Login
                  </Button>
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
